import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, RefreshControl, TouchableOpacity, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/providers/AuthProvider';
import { useChats, getChatDisplayName } from '@/modules/chat';
import { ChatListItem } from '@/modules/chat/components/ChatListItem';
import { Loader } from '@/components/Loader';
import { ChatWithDetails } from '@/modules/chat/types';

export default function ChatListScreen() {
  const router = useRouter();
  const { user, activeProfile } = useAuth();
  const userId = activeProfile?.id || user?.id;
  
  const [activeTab, setActiveTab] = useState<'direct' | 'groups'>('direct');
  const [refreshing, setRefreshing] = useState(false);

  const {
    chats,
    loading,
    error,
    refetch,
    getDirectChats,
    getGroupChats,
    leaveChat,
  } = useChats(userId);

  const directChats = useMemo(() => {
    if (!chats) return [];
    return getDirectChats();
  }, [chats, getDirectChats]);
  
  const groupChats = useMemo(() => {
    if (!chats) return [];
    return getGroupChats();
  }, [chats, getGroupChats]);

  const displayChats = activeTab === 'direct' ? directChats : groupChats;

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleNewChat = () => {
    if (activeTab === 'direct') {
      router.push('/chat/new-direct');
    } else {
      router.push('/chat/new-group');
    }
  };

  const handleChatPress = (chat: ChatWithDetails) => {
    router.push(`/(tabs)/chat/${chat.id}` as any);
  };

  const handleChatLongPress = (chat: any) => {
    const chatName = getChatDisplayName(chat, userId || '');
    const isGroup = chat.type === 'group' || chat.type === 'team';
    Alert.alert(
      chatName,
      isGroup
        ? 'Weet je zeker dat je deze groep wilt verlaten? Je ontvangt geen berichten meer.'
        : 'Weet je zeker dat je deze chat wilt verwijderen? Je ontvangt geen berichten meer.',
      [
        {
          text: isGroup ? 'Groep verlaten' : 'Chat verwijderen',
          style: 'destructive',
          onPress: async () => {
            try {
              await leaveChat(chat.id);
            } catch {
              Alert.alert('Fout', 'Kon chat niet verwijderen. Probeer het opnieuw.');
            }
          },
        },
        { text: 'Annuleer', style: 'cancel' },
      ]
    );
  };

  if (!userId) {
    return (
      <SafeAreaView className="flex-1 bg-gray-50 dark:bg-gray-900">
        <View className="flex-1 items-center justify-center">
          <Text className="text-gray-500 dark:text-gray-400">
            Please log in to view chats
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <View className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <View className="flex-row items-center justify-between mb-4">
          <Text className="text-2xl font-bold text-gray-900 dark:text-white">
            Chat
          </Text>
          <TouchableOpacity
            onPress={handleNewChat}
            className="w-10 h-10 rounded-full bg-purple-600 items-center justify-center"
            activeOpacity={0.7}
          >
            <Ionicons name="add" size={24} color="#fff" />
          </TouchableOpacity>
        </View>

        {/* Tabs */}
        <View className="flex-row bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
          <TouchableOpacity
            onPress={() => setActiveTab('direct')}
            className={`flex-1 py-2 rounded-lg items-center ${
              activeTab === 'direct'
                ? 'bg-white dark:bg-gray-600 shadow-sm'
                : ''
            }`}
            activeOpacity={0.7}
          >
            <Text
              className={`font-semibold ${
                activeTab === 'direct'
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Direct ({directChats.length})
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setActiveTab('groups')}
            className={`flex-1 py-2 rounded-lg items-center ${
              activeTab === 'groups'
                ? 'bg-white dark:bg-gray-600 shadow-sm'
                : ''
            }`}
            activeOpacity={0.7}
          >
            <Text
              className={`font-semibold ${
                activeTab === 'groups'
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Groups ({groupChats.length})
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Chat List */}
      {loading && !refreshing ? (
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      ) : error ? (
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color="#EF4444" />
          <Text className="text-red-500 text-center mt-4">{error}</Text>
          <TouchableOpacity
            onPress={refetch}
            className="mt-4 bg-purple-600 px-6 py-3 rounded-xl"
            activeOpacity={0.7}
          >
            <Text className="text-white font-semibold">Retry</Text>
          </TouchableOpacity>
        </View>
      ) : displayChats.length === 0 ? (
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons
            name={activeTab === 'direct' ? 'chatbubble-outline' : 'people-outline'}
            size={64}
            color="#9CA3AF"
          />
          <Text className="text-gray-500 dark:text-gray-400 text-center mt-4 text-lg">
            {activeTab === 'direct'
              ? 'No direct chats yet'
              : 'No group chats yet'}
          </Text>
          <Text className="text-gray-400 dark:text-gray-500 text-center mt-2">
            Tap the + button to start a conversation
          </Text>
        </View>
      ) : (
        <ScrollView
          className="flex-1 px-6"
          contentContainerStyle={{ paddingTop: 16, paddingBottom: 24 }}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor="#7C3AED"
            />
          }
          showsVerticalScrollIndicator={false}
        >
          {displayChats.map((chat) => (
            <ChatListItem
              key={chat.id}
              chat={chat}
              currentUserId={userId}
              onPress={handleChatPress}
              onLongPress={handleChatLongPress}
            />
          ))}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}
